import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import {
  SKIP_GBE_ERROR_NOTIFICATION,
  SKIP_GBE_VALIDATION_NOTIFICATION
} from '@core/interceptors/gbe-error.interceptor';
import { GbeEndPointV4 } from '@core/models/gbe/gbe-endpoint.enum';
import {
  TermsContent,
  TermsContentEdit,
  TermsLanguage,
  TermsPage,
  TermsPreviewRequest,
  TermsStrings,
  TermsTemplate,
  TermsTemplateEdit,
  TermsVersion,
  TermsVersionDetail,
  TermsVersionPublication
} from '@core/models/terms/terms-version.model';
import { GBE_BASE_URL } from '@core/tokens/gbe-url.token';

@Injectable({ providedIn: 'root' })
export class TermsService {
  private readonly gbeUrl = inject(GBE_BASE_URL);
  private readonly http = inject(HttpClient);

  // The terms page renders every failure inline (load errors, form error, delete error),
  // so the interceptor's toasts would only repeat them.
  private readonly options = {
    context: new HttpContext()
      .set(SKIP_GBE_ERROR_NOTIFICATION, true)
      .set(SKIP_GBE_VALIDATION_NOTIFICATION, true)
  };

  /** `versionDate` is sent as typed, in Paris wall clock. */
  publishTermsVersion$(
    versionDate: string,
    content: TermsContent
  ): Observable<TermsVersion> {
    return this.http.put<TermsVersion>(
      `${this.gbeUrl}/${GbeEndPointV4.TERMS_VERSION}`,
      { versionDate, content },
      this.options
    );
  }

  getTermsVersionDetail$(id: number): Observable<TermsVersionDetail> {
    return this.http.get<TermsVersionDetail>(
      `${this.gbeUrl}/${GbeEndPointV4.TERMS_VERSIONS}/${id}`,
      this.options
    );
  }

  /** `expectedUpdatedAt` is the version's `updatedAt` as loaded; GBE refuses the edit if it moved. */
  editTermsVersionContent$(
    id: number,
    body: { content: TermsContent; comment: string; expectedUpdatedAt: string }
  ): Observable<TermsVersionDetail> {
    return this.http.patch<TermsVersionDetail>(
      `${this.gbeUrl}/${GbeEndPointV4.TERMS_VERSIONS}/${id}/content`,
      body,
      this.options
    );
  }

  listTermsVersionContentEdits$(id: number): Observable<TermsContentEdit[]> {
    return this.http.get<TermsContentEdit[]>(
      `${this.gbeUrl}/${GbeEndPointV4.TERMS_VERSIONS}/${id}/edits`,
      this.options
    );
  }

  getTermsTemplate$(): Observable<TermsTemplate> {
    return this.http.get<TermsTemplate>(
      `${this.gbeUrl}/${GbeEndPointV4.TERMS_TEMPLATE}`,
      this.options
    );
  }

  saveTermsTemplate$(body: {
    html: string;
    strings: TermsStrings;
    comment: string;
    expectedUpdatedAt: string;
  }): Observable<TermsTemplate> {
    return this.http.put<TermsTemplate>(
      `${this.gbeUrl}/${GbeEndPointV4.TERMS_TEMPLATE}`,
      body,
      this.options
    );
  }

  listTermsTemplateEdits$(): Observable<TermsTemplateEdit[]> {
    return this.http.get<TermsTemplateEdit[]>(
      `${this.gbeUrl}/${GbeEndPointV4.TERMS_TEMPLATE}/edits`,
      this.options
    );
  }

  previewTerms$(request: TermsPreviewRequest): Observable<string> {
    return this.http
      .post<{
        html: string;
      }>(`${this.gbeUrl}/${GbeEndPointV4.TERMS_PREVIEW}`, request, this.options)
      .pipe(map((response) => response.html));
  }

  getCurrentTerms$(language: TermsLanguage): Observable<TermsPage> {
    return this.http.get<TermsPage>(`${this.gbeUrl}/${GbeEndPointV4.TERMS_CURRENT}`, {
      ...this.options,
      params: { language }
    });
  }

  listTermsVersions$(): Observable<TermsVersionPublication[]> {
    return this.http.get<TermsVersionPublication[]>(
      `${this.gbeUrl}/${GbeEndPointV4.TERMS_VERSIONS}`,
      this.options
    );
  }

  /** Returns the current state after the back end restored the previous version. */
  deleteTermsVersion$(id: number): Observable<TermsVersion> {
    return this.http.delete<TermsVersion>(
      `${this.gbeUrl}/${GbeEndPointV4.TERMS_VERSIONS}/${id}`,
      this.options
    );
  }
}
